/**
 * @/lib/api — Clerk-authenticated Hono API client.
 *
 * S-056 / FEAT-006. As of S-073 (Phase 9) this is the only HTTP
 * client in the SPA — every consumer migrated to the Clerk-
 * authenticated Hono path below.
 *
 * Three exports:
 *
 *   - `useApi()` — returns a typed `<T,>(path, init?) => Promise<T>`
 *     function. The Clerk JWT is fetched on every call (cached by
 *     Clerk; no work for callers) and attached as `Authorization:
 *     Bearer <jwt>`. The base URL comes from `VITE_API_URL`.
 *
 *   - `useApiQuery(key, path)` — TanStack Query wrapper.
 *
 *   - `useApiMutation(path, method)` — TanStack Mutation wrapper.
 *
 * Error handling rules (all three hooks):
 *
 *   - 401 → throw `ApiError` with status 401. Callers can catch this
 *     and trigger `openSignIn()` from Clerk; the default behavior in
 *     useApiQuery is to fail the query so RequireAuth's
 *     `<SignedOut>/<RedirectToSignIn>` kicks in on subsequent renders.
 *   - 403 → throw `ApiError` with status 403 and a friendly
 *     permissions message.
 *   - 5xx → throw `ApiError` with the server error text; consumers
 *     should surface a generic "Something went wrong" toast.
 *   - Other non-2xx → throw `ApiError` with the response text.
 *
 * The hooks intentionally avoid pulling in a toast library — surface
 * choice is up to the calling view. The thrown `ApiError` exposes
 * `status` and `body` so views can branch as needed.
 */

import { useCallback, useMemo } from "react";
import { useAuth, useClerk } from "@clerk/clerk-react";
import {
  useMutation,
  useQuery,
  type QueryKey,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

const apiBaseUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

if (!apiBaseUrl) {
  // eslint-disable-next-line no-console
  console.error(
    "[api] VITE_API_URL is not set. Add it to apps/web/.env (see .env.example)."
  );
}

// BUG-042 — re-exported from api-error.ts (split out so it can be imported
// standalone, without this module's import.meta.env dependency). Existing
// `import { ApiError } from "@/lib/api"` call sites are unaffected.
import { ApiError } from "@/lib/api-error";
export { ApiError };

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * Internal: turn a fetch Response into either a typed value or a
 * thrown ApiError. Lives outside the hook so useApiQuery/Mutation
 * can share it without re-creating closures on every render.
 */
async function unwrap<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!res.ok) {
    let message: string | undefined;
    if (res.status === 401) message = "Not signed in";
    else if (res.status === 403) message = "You don't have permission for that";
    else if (res.status >= 500) message = "Server error — please try again";
    throw new ApiError(res.status, text, message);
  }
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    // Plain-text endpoints (e.g. /health returns "OK") are valid; cast
    // and let the caller's generic type assert the shape.
    return text as unknown as T;
  }
}

/**
 * Build the fetch function once per render, capturing the current
 * Clerk getToken closure. Exposed as a hook because Clerk's getToken
 * is hook-only (it needs the React context).
 */
/**
 * Clerk JWT template name. Our Hono `requireAuth` middleware
 * expects custom claims (tenant_id, agencyclient_id, org_id,
 * org_role, email, name) that are NOT present in Clerk's default
 * session token. The "ccm-default" template injects them. Every
 * getToken() call in the SPA must request this template, or the
 * API will see a token with the wrong shape and fall through to
 * 401 / no_org_context errors.
 *
 * The template is configured in the Clerk dashboard:
 *   Configure → JWT templates → ccm-default
 * with claims: { user_id, tenant_id, agencyclient_id, org_id,
 * org_role, email, name } sourced from user.id / org metadata.
 */
const JWT_TEMPLATE = "ccm-default";

export function useApi() {
  const { getToken } = useAuth();
  return useCallback(
    async <T,>(path: string, init?: RequestInit): Promise<T> => {
      const token = await getToken({ template: JWT_TEMPLATE });
      const headers = new Headers(init?.headers ?? {});
      if (token) headers.set("Authorization", `Bearer ${token}`);
      // FormData must keep the browser-generated multipart boundary — never
      // force application/json on it (used by the library document file upload).
      if (
        init?.body &&
        !(init.body instanceof FormData) &&
        !headers.has("Content-Type")
      ) {
        headers.set("Content-Type", "application/json");
      }
      const url = `${apiBaseUrl}${path}`;
      const res = await fetch(url, { ...init, headers });
      return unwrap<T>(res);
    },
    [getToken]
  );
}

/**
 * `useApiQuery` — thin wrapper around `useQuery` that attaches the
 * Clerk JWT and uses `useApi` under the hood. Extra options are
 * forwarded to TanStack Query untouched.
 */
export function useApiQuery<T>(
  key: QueryKey,
  path: string,
  options?: Omit<UseQueryOptions<T, ApiError, T, QueryKey>, "queryKey" | "queryFn">
) {
  const api = useApi();
  return useQuery<T, ApiError, T, QueryKey>({
    queryKey: key,
    queryFn: () => api<T>(path),
    ...options,
  });
}

/**
 * `useApiMutation` — thin wrapper around `useMutation` for write
 * operations. `body` is JSON.stringified automatically when present.
 *
 * Example:
 *   const m = useApiMutation<Piece, { title: string }>("/pieces", "POST");
 *   m.mutate({ title: "Hello" });
 */
export function useApiMutation<TData, TBody = unknown>(
  path: string,
  method: Exclude<HttpMethod, "GET"> = "POST",
  options?: UseMutationOptions<TData, ApiError, TBody>
) {
  const api = useApi();
  return useMutation<TData, ApiError, TBody>({
    mutationFn: (body: TBody) =>
      api<TData>(path, {
        method,
        body: body === undefined ? undefined : JSON.stringify(body),
      }),
    ...options,
  });
}

/**
 * `useApiAuthSurface` — helper used by views that want to react to a
 * 401 by re-opening Clerk's sign-in modal. Returns a stable callback
 * that views can register via TanStack Query's `onError` hook.
 */
export function useApiAuthSurface() {
  const clerk = useClerk();
  return useMemo(
    () => ({
      handle: (err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          clerk.openSignIn?.();
        }
      },
    }),
    [clerk]
  );
}

// Re-export the resolved base URL for use in tooling / debug surfaces.
export const API_BASE_URL = apiBaseUrl;
